import React, { useEffect, useState } from "react";
import {
Box,
Card,
Typography,
TextField,
Button,
Stack,
Checkbox,
FormControlLabel,
Snackbar,
Alert
} from "@mui/material";

import { Colors } from "../utils/colors";
import { adminFetch } from "../services/adminFetch";
import TableLoadingOverlay from "../components/TableLoadingOverlay";
import ResponsiveDataTable from "../components/ResponsiveDataTable";
import {
  adminPageSx,
  contentCardSx,
  mobileActionGroupSx
} from "../utils/adminResponsive";

const initialForm = {
  title: "",
  description: "",
  facebook_url: "",
  instagram_url: "",
  snapchat_url: "",
  sort_order: 0,
  is_active: true
};

const getLinkCount = (group) =>
  [
    group.facebook_url,
    group.instagram_url,
    group.snapchat_url
  ].filter(Boolean).length;

export default function HowToPlay() {
  const [groups, setGroups] = useState([]);
  const [formData, setFormData] = useState(initialForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const loadGroups = async () => {
    setLoading(true);

    try {
      const data = await adminFetch("/api/admin/how-to-play", {
        method: "GET"
      });

      if (data?.status) {
        setGroups(data.groups || []);
      }
    } catch (error) {
      console.log(error);
    }

    setLoading(false);
  };

  useEffect(() => {
    loadGroups();
  }, []);

  const updateField = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const resetForm = () => {
    setFormData(initialForm);
    setEditingId(null);
  };

  const hasAtLeastOneLink = () =>
    [
      formData.facebook_url,
      formData.instagram_url,
      formData.snapchat_url
    ].some((value) => String(value || "").trim());

  const handleSubmit = async () => {
    setSaving(true);

    try {
      if (!hasAtLeastOneLink()) {
        showSnackbar("Add at least one social media link", "error");
        setSaving(false);
        return;
      }

      const endpoint = editingId ? `/api/admin/how-to-play/${editingId}` : "/api/admin/how-to-play";
      const method = editingId ? "PUT" : "POST";

      const data = await adminFetch(endpoint, {
        method,
        body: JSON.stringify(formData)
      });

      if (!data?.status) {
        showSnackbar(data?.message || "Failed to save group", "error");
        setSaving(false);
        return;
      }

      showSnackbar(editingId ? "How-to-play group updated successfully" : "How-to-play group created successfully");
      resetForm();
      await loadGroups();
    } catch (error) {
      console.log(error);
      showSnackbar("Server error", "error");
    }

    setSaving(false);
  };

  const groupColumns = [
    {
      key: "title",
      label: "Title",
      render: (group) => group.title
    },
    {
      key: "links",
      label: "Links Added",
      render: (group) => getLinkCount(group)
    },
    {
      key: "sort_order",
      label: "Sort Order",
      render: (group) => group.sort_order
    },
    {
      key: "status",
      label: "Status",
      render: (group) => (group.is_active ? "Active" : "Hidden")
    },
    {
      key: "actions",
      label: "Action",
      isActions: true,
      mobileValueSx: mobileActionGroupSx,
      render: (group) => (
        <Button
          size="small"
          variant="contained"
          onClick={() => {
            setEditingId(group.id);
            setFormData({
              title: group.title || "",
              description: group.description || "",
              facebook_url: group.facebook_url || "",
              instagram_url: group.instagram_url || "",
              snapchat_url: group.snapchat_url || "",
              sort_order: group.sort_order || 0,
              is_active: Boolean(group.is_active)
            });
          }}
        >
          Edit
        </Button>
      )
    }
  ];

  return (
    <Box
      sx={{
        ...adminPageSx
      }}
    >
      <Typography variant="h5" fontWeight={700} sx={{ color: Colors.accent, mb: 3 }}>
        How To Play Links
      </Typography>

      <Card sx={{ p: { xs: 2, sm: 4 }, mb: 3, ...contentCardSx }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 3 }}>
          {editingId ? "Update Link Group" : "Create Link Group"}
        </Typography>

        <Stack spacing={2}>
          <TextField
            label="Group Title"
            value={formData.title}
            onChange={(e) => updateField("title", e.target.value)}
            placeholder="How To Play, Registration Help, Cashout Guide"
            fullWidth
          />

          <TextField
            label="Description"
            value={formData.description}
            onChange={(e) => updateField("description", e.target.value)}
            fullWidth
            multiline
            rows={3}
          />

          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <TextField
              label="Facebook URL"
              value={formData.facebook_url}
              onChange={(e) => updateField("facebook_url", e.target.value)}
              fullWidth
            />
            <TextField
              label="Instagram URL"
              value={formData.instagram_url}
              onChange={(e) => updateField("instagram_url", e.target.value)}
              fullWidth
            />
          </Stack>

          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <TextField
              label="X URL"
              value={formData.snapchat_url}
              onChange={(e) => updateField("snapchat_url", e.target.value)}
              fullWidth
            />
            <TextField
              label="Sort Order"
              type="number"
              value={formData.sort_order}
              onChange={(e) => updateField("sort_order", e.target.value)}
              fullWidth
            />
          </Stack>

          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(formData.is_active)}
                onChange={(e) => updateField("is_active", e.target.checked)}
              />
            }
            label="Active on home page"
          />

          <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.78)" }}>
            Each title can hold its own Facebook, Instagram and X links. You can leave any platform empty and edit each group later.
          </Typography>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <Button
              variant="contained"
              sx={{ bgcolor: Colors.accent, color: "#003300", fontWeight: 700, width: { xs: "100%", sm: "auto" } }}
              onClick={handleSubmit}
              disabled={saving}
            >
              {editingId ? "Update Group" : "Create Group"}
            </Button>
            {editingId && (
              <Button variant="outlined" onClick={resetForm}>
                Cancel Edit
              </Button>
            )}
          </Stack>
        </Stack>
      </Card>

      <Card sx={{ p: 2, ...contentCardSx }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
          Existing Link Groups
        </Typography>

        <Box sx={{ position: "relative", minHeight: 320 }}>
          <ResponsiveDataTable
            columns={groupColumns}
            rows={groups}
            loading={loading}
            minWidth={700}
            emptyMinHeight={180}
            emptyMessage="No how-to-play link groups created yet"
            mobileTitle={(group) => group.title}
          />

          <TableLoadingOverlay loading={loading} />
        </Box>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
